import type { ReactNode } from 'react'
import { Card, LoadingState, ErrorState, EmptyState } from '@/components/ui'
import { cn } from '@/lib/utils.ts'

export interface StatTileProps {
  title: string
  subtitle?: string
  loading?: boolean
  error?: Error | null
  empty?: boolean
  emptyTitle?: string
  emptyMessage?: string
  children?: ReactNode
  className?: string
  headerRight?: ReactNode
}

export function StatTile({
  title,
  subtitle,
  loading,
  error,
  empty,
  emptyTitle = 'Keine Daten',
  emptyMessage = 'Noch keine Daten für diesen Zeitraum.',
  children,
  className,
  headerRight,
}: StatTileProps) {
  return (
    <Card className={cn('flex flex-col', className)}>
      <Card.Header>
        <div className="flex items-start justify-between gap-2">
          <div>
            <Card.Title>{title}</Card.Title>
            {subtitle && <p className="text-caption text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
          {headerRight && <div className="shrink-0">{headerRight}</div>}
        </div>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <LoadingState size="sm" label="Wird geladen…" />
        ) : error ? (
          <ErrorState
            size="sm"
            title="Fehler"
            description={(error as Error).message}
          />
        ) : empty ? (
          <EmptyState size="sm" title={emptyTitle} description={emptyMessage} />
        ) : (
          children
        )}
      </Card.Body>
    </Card>
  )
}

// ─── Filter controls used by tiles ──────────────────────────────────────────

export const PERIOD_OPTIONS = [
  { label: '30 Tage', days: 30 },
  { label: '90 Tage', days: 90 },
  { label: '6 Monate', days: 180 },
  { label: '1 Jahr', days: 365 },
]

export const MONTH_OPTIONS = [
  { label: '3 Monate', months: 3 },
  { label: '6 Monate', months: 6 },
  { label: '12 Monate', months: 12 },
  { label: '24 Monate', months: 24 },
]

interface PeriodSelectProps {
  value: number
  options: { label: string; days?: number; months?: number }[]
  onChange: (v: number) => void
}

export function PeriodSelect({ value, options, onChange }: PeriodSelectProps) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="text-caption border border-gray-200 rounded px-2 py-1 bg-white text-gray-600 focus:outline-none focus:ring-1 focus:ring-brand-400"
    >
      {options.map((o) => (
        <option key={o.days ?? o.months} value={o.days ?? o.months}>
          {o.label}
        </option>
      ))}
    </select>
  )
}
